import * as React from "react"
import { router } from "@inertiajs/react"
import { Search, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

type SearchResult = {
  readonly id: number
  readonly lead_name: string
  readonly lead_email: string
  readonly company: string
  readonly subject: string
  readonly status: string
  readonly client_name: string | null
  readonly snippet: string
}

type SearchResponse = {
  readonly replies?: readonly SearchResult[]
}

export function CommandPalette() {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const [results, setResults] = React.useState<readonly SearchResult[]>([])
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        setOpen(true)
      }
      if (event.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  React.useEffect(() => {
    if (!open) return
    inputRef.current?.focus()
  }, [open])

  React.useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults([])
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      const url = new URL("/api/search", window.location.origin)
      url.searchParams.set("q", query.trim())
      fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } })
        .then((response) => (response.ok ? response.json() : { replies: [] }))
        .then((data: SearchResponse) => setResults(data.replies ?? []))
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === "AbortError") return
          throw error
        })
    }, 180)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [open, query])

  const selectResult = (reply: SearchResult) => {
    setOpen(false)
    setQuery("")
    router.get("/inbox", { selected: reply.id })
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-ink-display/35 p-4 backdrop-blur-sm">
      <div className="mx-auto mt-20 max-w-2xl rounded-lg border border-hairline bg-page shadow-xl">
        <div className="flex items-center gap-3 border-b border-hairline p-4">
          <Search className="h-4 w-4 text-ink-muted" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search replies"
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close command palette">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-2">
          {results.map((reply) => (
            <button
              key={reply.id}
              type="button"
              onClick={() => selectResult(reply)}
              className="block w-full cursor-pointer rounded-md px-3 py-3 text-left hover:bg-surface"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-ink-display">{reply.lead_name}</span>
                <span className="shrink-0 text-xs text-ink-muted">{reply.status}</span>
              </div>
              <div className="mt-1 truncate text-sm text-ink-muted">
                {[reply.client_name, reply.company, reply.lead_email].filter(Boolean).join(" · ")}
              </div>
              <p className="mt-1 line-clamp-1 text-sm">{reply.subject || reply.snippet}</p>
            </button>
          ))}
          {query.trim().length >= 2 && results.length === 0 && (
            <div className="p-6 text-center">
              <p>No replies found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
